export type RosettaResponseFormat =
  | { type: 'text' }
  | {
      type: 'json_object'
      schema?: Record<string, unknown>
    }
  | {
      type: 'json_schema'
      json_schema: {
        name?: string
        strict?: boolean
        schema: Record<string, unknown>
      }
    }

export type VideoProcessingRunKind = 'generic' | 'video_to_workflow'
export type VideoProcessingRunStatus = 'queued' | 'running' | 'completed' | 'error' | 'cancelled'

export interface VideoProcessingSource {
  type: 'fileId' | 'upload'
  fileId?: string
  filename: string
}

export interface VideoProcessingOptionsInput {
  clipStartSec?: number
  clipEndSec?: number
  sampleFps?: number
  maxWidth?: number
  maxHeight?: number
  framesPerBatch?: number
  maxTotalFrames?: number
  priorBatchWindow?: number | 'all'
  maxTokensPerBatch?: number
  maxTokensFinal?: number
  maxBatchBytes?: number
  requestTimeoutMs?: number
  maxRetries?: number
}

export interface GenericVideoPromptInput {
  batchSystemPrompt?: string
  batchInstructions: string
  finalSystemPrompt?: string
  finalInstructions: string
  batchResponseFormat?: RosettaResponseFormat
  finalResponseFormat?: RosettaResponseFormat
}

export interface GenericVideoBatchReport {
  batchIndex: number
  startSec: number
  endSec: number
  frameCount: number
  rawText: string
  parsed?: Record<string, unknown> | Array<unknown> | null
  usage?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
  }
}

export interface GenericVideoFinalReport {
  rawText: string
  parsed?: Record<string, unknown> | Array<unknown> | null
  usage?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
  }
}

export interface GenericVideoReportResponse {
  source: VideoProcessingSource
  metadata?: {
    durationSec: number
    fps: number
    width: number
    height: number
  }
  framesAnalyzed: number
  batches: GenericVideoBatchReport[]
  finalResult?: GenericVideoFinalReport
  totalUsage?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
  }
  warnings: string[]
}

export interface VideoToWorkflowStep {
  index: number
  description: string
  approxTimestampSec?: number
  uiElements?: string[]
}

export interface VideoToWorkflowBatchSummary {
  batchIndex: number
  startSec: number
  endSec: number
  text: string
  usage?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
  }
}

export interface VideoToWorkflowReportResponse {
  source: VideoProcessingSource
  durationSec?: number
  framesAnalyzed: number
  identifiedServices?: string[]
  goalSummary?: string
  steps: VideoToWorkflowStep[]
  rawSynthesis?: string
  batchSummaries: VideoToWorkflowBatchSummary[]
  totalUsage?: {
    promptTokens?: number
    completionTokens?: number
    totalTokens?: number
  }
  warnings: string[]
}

export type VideoProcessingResult = GenericVideoReportResponse | VideoToWorkflowReportResponse
export type VideoProcessingPartialResult = GenericVideoReportResponse | VideoToWorkflowReportResponse

export interface VideoProcessingErrorPayload {
  message: string
  stage?: string
  failedBatchIndex?: number
  attemptCount?: number
  partialReport?: VideoProcessingPartialResult
}

export interface VideoProcessingRequestSummary {
  model: string
  source: VideoProcessingSource
  options?: VideoProcessingOptionsInput
  prompt?: GenericVideoPromptInput
}

export interface VideoProcessingRunRecord {
  runId: string
  ownerUserId: string
  kind: VideoProcessingRunKind
  source: VideoProcessingSource
  modelName: string
  status: VideoProcessingRunStatus
  requestSummary: VideoProcessingRequestSummary
  result?: VideoProcessingResult
  partialResult?: VideoProcessingPartialResult
  errorMessage?: string
  errorStage?: string
  failedBatchIndex?: number
  attemptCount?: number
  createdAt: number
  updatedAt: number
  startedAt?: number
  completedAt?: number
  cancelledAt?: number
  internalTempFilePath?: string | null
}

export interface VideoProcessingRunStreamEvent {
  type: 'status' | 'result' | 'error' | 'cancelled'
  runId: string
  status?: VideoProcessingRunStatus
  result?: VideoProcessingResult
  error?: VideoProcessingErrorPayload
  message?: string
  updatedAt: number
}

export interface VideoToWorkflowUploadRequest {
  model: string
  options?: VideoProcessingOptionsInput
}

export interface VideoProcessingSnapshotFrame {
  type: 'snapshot'
  record: VideoProcessingRunRecord
}

export interface VideoProcessingStatusFrame {
  type: 'status'
  runId: string
  status: VideoProcessingRunStatus
  updatedAt: number
}

export interface VideoProcessingResultFrame {
  type: 'result'
  runId: string
  result: VideoProcessingResult
  updatedAt: number
}

export interface VideoProcessingRunErrorFrame {
  type: 'error'
  runId: string
  error: VideoProcessingErrorPayload
  updatedAt: number
}

export interface VideoProcessingTransportErrorFrame {
  type: 'error'
  message: string
}

export interface VideoProcessingCancelledFrame {
  type: 'cancelled'
  runId: string
  message: string
  updatedAt: number
}

export type VideoProcessingStreamFrame =
  | VideoProcessingSnapshotFrame
  | VideoProcessingStatusFrame
  | VideoProcessingResultFrame
  | VideoProcessingRunErrorFrame
  | VideoProcessingTransportErrorFrame
  | VideoProcessingCancelledFrame

export interface WorkflowVideoReviewDraft {
  runId: string
  markdown: string
  updatedAt: number
}

export function isVideoToWorkflowReportResponse(result: VideoProcessingResult): result is VideoToWorkflowReportResponse {
  return 'batchSummaries' in result && 'steps' in result
}

export function isVideoProcessingSnapshotFrame(frame: VideoProcessingStreamFrame): frame is VideoProcessingSnapshotFrame {
  return frame.type === 'snapshot'
}

export function isVideoProcessingStatusFrame(frame: VideoProcessingStreamFrame): frame is VideoProcessingStatusFrame {
  return frame.type === 'status'
}

export function isVideoProcessingResultFrame(frame: VideoProcessingStreamFrame): frame is VideoProcessingResultFrame {
  return frame.type === 'result'
}

export function isVideoProcessingRunErrorFrame(frame: VideoProcessingStreamFrame): frame is VideoProcessingRunErrorFrame {
  return frame.type === 'error' && 'runId' in frame
}

export function isVideoProcessingTransportErrorFrame(
  frame: VideoProcessingStreamFrame
): frame is VideoProcessingTransportErrorFrame {
  return frame.type === 'error' && !('runId' in frame)
}

export function isVideoProcessingCancelledFrame(
  frame: VideoProcessingStreamFrame
): frame is VideoProcessingCancelledFrame {
  return frame.type === 'cancelled'
}
